import { ChangeEvent, FC, useEffect, useState } from 'react';

import useDC from '@eneco/dc/useDC';
import { addressMachine } from '@eneco/flows/machines';
import { Box, Grid, InputSelect, InputText, NotificationBox, Stack } from '@eneco/sparky';
import { useMachine } from '@xstate/react';
import { FieldErrors, UseFormGetFieldState, UseFormGetValues, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { useDebounce } from 'react-use';

import { CompanyDetailsFormFields } from '../types';

interface AddressFieldsWithAddressFinderProps {
  register: UseFormRegister<CompanyDetailsFormFields>;
  errors: FieldErrors<CompanyDetailsFormFields>;
  setValue: UseFormSetValue<CompanyDetailsFormFields>;
  getValues: UseFormGetValues<CompanyDetailsFormFields>;
  getFieldState: UseFormGetFieldState<CompanyDetailsFormFields>;
  isValidating: boolean;
  defaultValues?: {
    postalCode?: string;
    houseNumber?: number;
    houseNumberSuffix?: string;
  };
}

export const AddressFieldsWithAddressFinder: FC<AddressFieldsWithAddressFinderProps> = ({
  register,
  errors,
  setValue,
  getValues,
  getFieldState,
  isValidating,
  defaultValues,
}) => {
  const { businessUnit, label } = useDC();
  const [addressState, sendToAddressMachine] = useMachine(addressMachine);

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [hasFormValidationErrors, setHasFormValidationErrors] = useState(false);

  const [postCodeToValidate, setPostCodeToValidate] = useState(defaultValues?.postalCode);
  const [houseNumberToValidate, setHouseNumberToValidate] = useState(defaultValues?.houseNumber);
  const [houseNumberSuffixToValidate, setHouseNumberSuffixToValidate] = useState(defaultValues?.houseNumberSuffix);

  const [shouldDisableHouseNumberSuffix, setShouldDisableHouseNumberSuffix] = useState(
    !defaultValues?.houseNumberSuffix || !!addressState.context.houseNumberSuffixes,
  );

  useDebounce(
    () => {
      if (postCodeToValidate && houseNumberToValidate) {
        sendToAddressMachine({
          type: 'GET_ADDRESS',
          config: {
            businessUnit,
            label,
          },
          values: {
            postalCode: postCodeToValidate,
            houseNumber: houseNumberToValidate,
          },
        });
      }
    },
    500,
    [postCodeToValidate, houseNumberToValidate],
  );

  useEffect(() => {
    const isSuccess = addressState.matches('SUCCESS');

    setIsLoading(addressState.matches('FETCHING'));
    setIsSuccessful(isSuccess);
    setIsFailed(addressState.matches('ERROR') || addressState.matches('NOT_FOUND'));

    setShouldDisableHouseNumberSuffix(!isSuccess);

    if (isSuccess) {
      const { street, city } = addressState.context;

      // set values of the hidden fields
      setValue('city', city ?? '');
      setValue('street', street ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressState]);

  useEffect(() => {
    const { postalCode, houseNumber, houseNumberSuffix } = getValues();

    setHasFormValidationErrors(!(!errors.houseNumber && !errors.postalCode));
    setHouseNumberSuffixToValidate(houseNumberSuffix);

    if (!isValidating && postalCode && houseNumber) {
      if (getFieldState('postalCode').isTouched || getFieldState('houseNumber').isTouched) {
        if (!getFieldState('postalCode').error && !getFieldState('houseNumber').error) {
          if (postalCode !== postCodeToValidate || houseNumber !== houseNumberToValidate) {
            setShouldDisableHouseNumberSuffix(true);

            // reset values of the form fields
            setValue('houseNumberSuffix', '');
            setValue('city', '');
            setValue('street', '');

            // due to use of a "debounce" we update the state right away
            setIsSuccessful(false);
            setIsLoading(true);
            setIsFailed(false);

            setHouseNumberSuffixToValidate('');
          }

          setPostCodeToValidate(postalCode);
          setHouseNumberToValidate(houseNumber);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidating]);

  return (
    <Stack direction="column" gap={6}>
      <Grid columns="3" columnGap="3" gridTemplateColumns="2fr 2fr 1.6fr">
        <InputText
          {...register('postalCode', {
            onChange: (event: ChangeEvent<HTMLInputElement>) => {
              const target = event.target;
              const position = target.selectionStart;

              target.value = target?.value?.toUpperCase();
              target.selectionEnd = position;
            },
          })}
          error={errors.postalCode?.message as string}
          label="Postcode"
          placeholder="1234 AB"
        />

        <InputText
          {...register('houseNumber')}
          error={errors.houseNumber?.message as string}
          label="Huisnummer"
          placeholder="123"
        />

        <InputSelect
          {...register('houseNumberSuffix')}
          label="Toevoeging"
          placeholder=""
          isDisabled={hasFormValidationErrors || shouldDisableHouseNumberSuffix}
          options={[
            { label: '-', value: '' },
            ...(addressState?.context?.houseNumberSuffixes ?? []).map(suffix => {
              return {
                label: suffix,
                value: suffix,
              };
            }),
          ]}
        />
      </Grid>

      {!isLoading && !hasFormValidationErrors && isSuccessful && (
        <Box>
          {addressState.context.street} {addressState.context.houseNumber}
          {houseNumberSuffixToValidate && ` ${houseNumberSuffixToValidate}`}, {addressState.context.postalCode}{' '}
          {addressState.context.city}
        </Box>
      )}

      {!isLoading && !hasFormValidationErrors && isFailed && (
        <NotificationBox isAlert variant="error" text="Het opgegeven adres is niet gevonden. Controleer je invoer." />
      )}

      <input {...register('city')} type="hidden" />
      <input {...register('street')} type="hidden" />
    </Stack>
  );
};
